import React from "react";
import { getUiOptions, type RJSFSchema, type UiSchema } from "@rjsf/utils";
import { isDevelopment } from "common/utils/tools";
import { useStatusToasts } from "../../../../../../../hooks/useStatusToasts";
import { Button } from "../../../../../../Common/Button";
import { Icon } from "../../../../../../Common/Icons/LucideIcons";
import { Body } from "../../../../../../Common/Typography";
import {
  inputTypeToFieldsListLabelMap,
  type SubmissionsBuilderFieldTypeWithSchema,
} from "../../../SubmissionsBuilder/constants";
import type { PathArray } from "../../FieldPathContext/FieldPathContext";
import {
  getSchemaFormPath,
  getUiSchemaFormPath,
  useBuilderWatch,
} from "../../form";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "../../SubmissionsBuilderContext";
import { FieldSettingsHeader } from "./__styles__/FieldSettingsView";
import { CheckboxWidgetSettings } from "./CheckboxWidgetSettings";

export type InputSettingsFormProps = {
  fieldPath: PathArray;
  isReadOnly: boolean;
  isModuleInput: boolean;
  inputType: string | null;
};

function NotImplemented({ inputType }: InputSettingsFormProps) {
  return <div>Not implemented: {inputType}</div>;
}

function InvalidInputType({ inputType }: InputSettingsFormProps) {
  return <div>Invalid input type: {inputType}</div>;
}

const inputTypeToSettingsFormMap = {
  checkbox: CheckboxWidgetSettings,
  DamageDegree: NotImplemented,
  date: NotImplemented,
  DocumentUploader: NotImplemented,
  group: NotImplemented,
  multiSelect: NotImplemented,
  number: NotImplemented,
  NumberWithUnits: NotImplemented,
  PropertyMarketValue: NotImplemented,
  select: NotImplemented,
  Tags: NotImplemented,
  text: NotImplemented,
  textarea: NotImplemented,
  UniqueId: NotImplemented,
  User: NotImplemented,
} as const satisfies Record<
  string,
  React.ComponentType<InputSettingsFormProps>
>;

type InputType = keyof typeof inputTypeToSettingsFormMap;

function getFieldSettingsFormComponent(inputType: string | null) {
  if (!inputTypeToSettingsFormMap[inputType as InputType]) {
    return InvalidInputType;
  }

  return inputTypeToSettingsFormMap[inputType as InputType];
}

function getInputTypeTitle(inputType: string | null) {
  if (inputType === "DamageDegree") {
    return "Damage degree";
  }

  if (
    !inputTypeToFieldsListLabelMap[
      inputType as SubmissionsBuilderFieldTypeWithSchema
    ]
  ) {
    return "Unknown";
  }

  return inputTypeToFieldsListLabelMap[
    inputType as SubmissionsBuilderFieldTypeWithSchema
  ];
}

function getInputType(schema: RJSFSchema, uiSchema: UiSchema) {
  const uiOptions = getUiOptions(uiSchema);

  if (uiOptions.widget) {
    return uiOptions.widget as string;
  }
  if (uiOptions.field) {
    return uiOptions.field as string;
  }
  if (schema?.type === "object") {
    return "group";
  }

  return null;
}

type FieldSettingsViewProps = {
  selectedField: PathArray;
};

const TEMP_isModuleInput = false;

export function FieldSettingsView({ selectedField }: FieldSettingsViewProps) {
  const { selectField } = useSubmissionsBuilderContext();
  const [schema, uiSchema] = useBuilderWatch([
    getSchemaFormPath(selectedField),
    getUiSchemaFormPath(selectedField),
  ]);

  const inputType = getInputType(schema, uiSchema);
  const title = getInputTypeTitle(inputType);
  const InputSettingsForm = getFieldSettingsFormComponent(inputType);

  const [mode] = useSubmissionsBuilderMode();
  const isReadOnly = mode !== SUBMISSIONS_BUILDER_MODE.BUILD;

  return (
    <>
      <FieldSettingsHeader role="none">
        <Button
          aria-label="Back to form fields"
          style={{ padding: 0 }}
          styleVariant="ghost"
          onClick={() => {
            selectField(undefined);
          }}
        >
          <Icon color="contentSecondary" iconName="chevron-left" size={16} />
        </Button>
        <Body size="large" type="emphasis">
          {title} field properties
        </Body>
        {isDevelopment() && (
          <CopyFormDataPathToClipboardButton selectedField={selectedField} />
        )}
      </FieldSettingsHeader>
      <InputSettingsForm
        fieldPath={selectedField}
        inputType={inputType}
        isReadOnly={isReadOnly}
        isModuleInput={TEMP_isModuleInput}
      />
    </>
  );
}

type CopyFormDataPathToClipboardButtonProps = {
  selectedField: PathArray;
};

function CopyFormDataPathToClipboardButton({
  selectedField,
}: CopyFormDataPathToClipboardButtonProps) {
  const toasts = useStatusToasts();

  return (
    <Icon
      aria-label="Copy form data path to clipboard"
      color="contentSecondary"
      iconName="copy"
      size={16}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(
            `formData.${selectedField.join(".")}`,
          );
          toasts.addSuccessToast("Copied form data path to clipboard");
        } catch (error) {
          toasts.addErrorToast("Could not copy form data path to clipboard");
        }
      }}
    />
  );
}
